import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { UserPlus, Loader2, Check } from "lucide-react";

/**
 * AddToGroupDropdown — the "Add to…" picker from the SearchPalette,
 * rendered as a portal so it can never be clipped by the palette's
 * own `overflow-y-auto` parent.
 *
 * Steve's bug report (Feb 2026): "drop down screen visibility in
 * search function" — the previous implementation used <details> nested
 * inside the scrolling results list, which meant the dropdown body
 * disappeared whenever a result sat near the viewport edge.
 *
 * Props:
 *   ownedGroups   — array of {group_id, name} the user can invite into
 *   onPick(g)     — called with the chosen group
 *   busyKey       — string (`{person_id}-{group_id}`) currently sending
 *   userId        — the person being invited (used to build the busy key)
 *   testIdPrefix  — e.g. `search-invite-user_abc`
 */
export default function AddToGroupDropdown({
  ownedGroups,
  onPick,
  busyKey,
  userId,
  testIdPrefix,
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 240 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Position the portal under the trigger button, clamped to the viewport
  // so it never spills off the right edge on a narrow phone.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = Math.min(240, window.innerWidth - 24);
    const left = Math.min(rect.right - width, window.innerWidth - width - 12);
    const top = Math.min(rect.bottom + 4, window.innerHeight - 20);
    setPos({ top, left: Math.max(12, left), width });
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="bd-btn bd-btn-ghost flex items-center gap-1"
        style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem", whiteSpace: "nowrap" }}
        data-testid={testIdPrefix}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <UserPlus size={12} /> Add to…
      </button>
      {open && createPortal(
        <div
          ref={dropdownRef}
          className="bd-surface"
          role="menu"
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            width: pos.width,
            zIndex: 200, // above SearchPalette's z-50 and any banners
            padding: "0.35rem",
            borderRadius: "var(--radius-card)",
            background: "var(--bg-elev, var(--bg))",
            boxShadow: "0 16px 36px -8px rgba(0,0,0,0.45)",
            maxHeight: "min(50vh, 18rem)",
            overflowY: "auto",
          }}
          data-testid={`${testIdPrefix}-menu`}
        >
          {ownedGroups.length === 0 && (
            <div className="text-xs px-2 py-2" style={{ color: "var(--text-dim)" }}>
              You don&apos;t own any groups yet.
            </div>
          )}
          {ownedGroups.map((g) => {
            const k = `${userId}-${g.group_id}`;
            const isBusy = busyKey === k;
            return (
              <button
                key={g.group_id}
                type="button"
                onClick={() => { onPick(g); setOpen(false); }}
                disabled={isBusy}
                className="w-full flex items-center justify-between gap-2 text-left px-2 py-1.5 rounded text-sm hover:opacity-80"
                style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--text)" }}
                data-testid={`${testIdPrefix}-${g.group_id}`}
              >
                <span className="truncate">{g.name}</span>
                {isBusy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} style={{ opacity: 0 }} />}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}
